/**
 * CNPJ helpers.
 *
 * Used heavily by the self-signup flow: the company types its CNPJ and the
 * platform fills in razão social, address, CNAE and partners from the local
 * Receita Federal dataset. Reducing that form to three fields is the single
 * biggest conversion win available to this product.
 */

/** Strips everything that is not a digit. */
export function limparCnpj(valor: string): string {
  return String(valor ?? '').replace(/\D/g, '')
}

/** Formats 14 digits as 00.000.000/0000-00. Returns the input if not 14 digits. */
export function formatarCnpj(valor: string): string {
  const d = limparCnpj(valor)
  if (d.length !== 14) return String(valor ?? '')
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
}

function digitoVerificador(base: string, pesoInicial: number): number {
  let soma = 0
  let peso = pesoInicial
  for (const ch of base) {
    soma += Number(ch) * peso
    peso = peso === 2 ? 9 : peso - 1
  }
  const resto = soma % 11
  return resto < 2 ? 0 : 11 - resto
}

/**
 * Validates a CNPJ by its check digits.
 * Rejects repeated-digit sequences (00000000000000 etc.), which pass the
 * arithmetic but are never real registrations.
 */
export function validarCnpj(valor: string): boolean {
  const d = limparCnpj(valor)
  if (d.length !== 14) return false
  if (/^(\d)\1{13}$/.test(d)) return false

  const dv1 = digitoVerificador(d.slice(0, 12), 5)
  if (dv1 !== Number(d[12])) return false

  const dv2 = digitoVerificador(d.slice(0, 13), 6)
  return dv2 === Number(d[13])
}

/** The 8-digit root shared by every establishment of the same company. */
export function raizCnpj(valor: string): string {
  return limparCnpj(valor).slice(0, 8)
}

/** True when both CNPJs belong to the same company (same root). Used for dedupe. */
export function mesmaEmpresa(a: string, b: string): boolean {
  const ra = raizCnpj(a)
  return ra.length === 8 && ra === raizCnpj(b)
}
